import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { AuthLegalLine } from "@/components/AuthLegalLine";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, MailCheck, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useVerifyResend from "@/hooks/useVerifyResend";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, refresh } = useAuth();
  const [state, setState] = useState(token ? "loading" : "no-token");
  const [msg, setMsg] = useState("");
  const { state: resendState, message: resendMsg, resend, cooldownLeft, isBusy } = useVerifyResend();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.post("/auth/verify/confirm", { token })
      .then(async () => {
        if (cancelled) return;
        setState("ok");
        // Refresh the session so email_verified flips to true immediately.
        try { await refresh(); } catch {}
      })
      .catch((e) => {
        if (cancelled) return;
        setState("error");
        setMsg(formatApiError(e.response?.data?.detail) || "Verification failed.");
      });
    return () => { cancelled = true; };
  }, [token, refresh]);

  const canResend = !!user; // /auth/verify/request requires authentication.

  return (
    <div className="max-w-md mx-auto px-6 pt-32 pb-16" data-testid="verify-email-page">
      <div className="card-elegant p-8 text-center">
        {state === "loading" && (
          <p className="text-[color:var(--text-muted)]">Verifying…</p>
        )}

        {state === "ok" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-[color:var(--verified)]" />
            <h1 className="font-serif text-3xl mt-3" data-testid="verify-success">Email verified</h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-2" data-testid="verify-success-message">
              {user?.role === "vendor"
                ? "Your email has been verified. You can now receive quote requests, manage bookings, and access payment features."
                : user?.role === "admin"
                ? "Your email has been verified successfully."
                : "Your email has been verified. You can now request quotes, book vendors, and make payments."}
            </p>
            <Link to="/"><Button className="btn-sage rounded-full h-11 mt-6">Continue</Button></Link>
          </>
        )}

        {state === "error" && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-[#D9583A]" />
            <h1 className="font-serif text-3xl mt-3" data-testid="verify-error">
              Verification link is invalid or expired
            </h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-2">{msg}</p>
            <ResendBlock
              canResend={canResend}
              resend={resend}
              resendState={resendState}
              resendMsg={resendMsg}
              cooldownLeft={cooldownLeft}
              isBusy={isBusy}
              cta="Send a new verification email"
            />
          </>
        )}

        {state === "no-token" && (
          <>
            <MailCheck className="w-10 h-10 mx-auto text-[#F97316]" />
            <h1 className="font-serif text-3xl mt-3">Verify your email</h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-2">
              Open the link from your email, or send yourself a fresh verification link below.
            </p>
            <ResendBlock
              canResend={canResend}
              resend={resend}
              resendState={resendState}
              resendMsg={resendMsg}
              cooldownLeft={cooldownLeft}
              isBusy={isBusy}
              cta="Send verification email"
            />
          </>
        )}
      </div>
      <AuthLegalLine className="mt-4 text-center" />
    </div>
  );
}

function ResendBlock({ canResend, resend, resendState, resendMsg, cooldownLeft, isBusy, cta }) {
  if (!canResend) {
    return (
      <p className="text-xs text-[color:var(--text-muted)] mt-6">
        Please <Link to="/login" className="link-underline">log in</Link> to receive a new verification email.
      </p>
    );
  }
  const success = resendState === "sent";
  const error = resendState === "error";
  return (
    <>
      <Button
        onClick={resend}
        disabled={isBusy}
        data-testid="resend-verify-btn"
        className="rounded-full h-11 mt-6 bg-[#1A1A1A] text-white disabled:opacity-60"
      >
        <Mail className="w-4 h-4 mr-1.5" />
        {resendState === "sending"
          ? "Sending…"
          : cooldownLeft > 0
          ? `Resend in ${cooldownLeft}s`
          : cta}
      </Button>
      {(success || error) && (
        <p
          data-testid="resend-verify-msg"
          className={"text-xs mt-3 " + (error ? "text-red-600" : "text-emerald-700")}
        >
          {resendMsg}
        </p>
      )}
    </>
  );
}
